import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Check, File, Trash, ArrowRight } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Image } from "@themes/pollinator/ui/Image";
import { DataTable } from "@themes/pollinator/ui/DataTable";
import { OrganizationDocument } from "@core/features/organization";

export type Props = {
  /** List of logo documents to display */
  logos: OrganizationDocument[];
  /** Handler for setting a logo as the main logo */
  onSetMainLogo: (mediaId: number) => void;
  /** Handler for deleting a logo */
  onDelete: (mediaId: number) => void;
  /** Whether the set main logo mutation is pending */
  isSettingMainLogo?: boolean;
  /** Whether the delete mutation is pending */
  isDeleting?: boolean;
};

/**
 * Table component for displaying organization logos.
 * Allows setting a logo as main and deleting logos.
 */
export function LogosTable({
  logos,
  onSetMainLogo,
  onDelete,
  isSettingMainLogo = false,
  isDeleting = false,
}: Props) {
  const columns = useMemo<ColumnDef<OrganizationDocument>[]>(
    () => [
      {
        accessorKey: "name",
        header: () => "File Name",
        cell: ({ getValue, row }) => (
          <a href={row.original.file} target="_blank" rel="noopener noreferrer">
            <Button variant="link" iconComponent={File}>
              {getValue<string>()}
            </Button>
          </a>
        ),
      },
      {
        accessorKey: "file",
        header: () => "Preview",
        maxSize: 100,
        cell: ({ row }) => (
          <a href={row.original.file} target="_blank" rel="noopener noreferrer">
            <Image
              src={row.original.file}
              alt={`${row.original.name} preview`}
              className="h-14 w-auto max-w-full object-contain"
              imageClassName="rounded-md"
            />
          </a>
        ),
      },
      {
        accessorKey: "main_logo",
        header: () => "",
        cell: ({ getValue, row }) => {
          const isMainLogo = getValue<boolean>();

          return isMainLogo ? (
            <Button variant="link" iconComponent={Check} disabled>
              Main Logo
            </Button>
          ) : (
            <Button
              variant="link"
              iconComponent={ArrowRight}
              onClick={() => onSetMainLogo(row.original.id)}
              disabled={isSettingMainLogo}
              className="flex-shrink-0 flex-grow-0 whitespace-nowrap"
            >
              Set as main logo
            </Button>
          );
        },
      },
      {
        accessorKey: "id",
        header: () => "",
        cell: ({ row }) => (
          <Button
            variant="link"
            iconComponent={Trash}
            onClick={() => onDelete(row.original.id)}
            disabled={isDeleting}
          >
            Delete
          </Button>
        ),
      },
    ],
    [onSetMainLogo, onDelete, isSettingMainLogo, isDeleting]
  );

  return (
    <DataTable
      data={logos}
      columns={columns}
      isWithSearch={false}
      initialPageSize={25}
      data-testid="logos-table"
    />
  );
}

export default LogosTable;

